import { FunctionComponent, useEffect, useState } from "react";
import {
  defaultPurchaseValueV2,
  PurchaseVoucherModelV2,
} from "../../models/purchase_model";
import { UTCToLocalDate } from "../../../../utils/date_util";
import moment from "moment";
import { InwardModel } from "../../../inventory/models/inventory_voucher";
import {
  ModalAlignment,
  ModalType,
} from "../../../../context_providers/modal/modal_context";
import Icon from "@mdi/react";
import { mdiChevronLeft, mdiRefresh } from "@mdi/js";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import { useHistory, useParams } from "react-router-dom";
import PartySearchField from "../../parties/party_search_field";
import DrawerWidget from "../../../../context_providers/modal/drawer_widget";
import {
  AttachmentModel,
  PurchaseOrderModelV2,
} from "../../../purchase_order/model/purchase_order_model";
import { formToObject } from "../../../../utils/formDataToJson";
import { currencies } from "../../models/party_model";
import GRNItemCard from "./components/grn_item_card";
import GRNSummaryCard from "./components/grn_summary_card";
import {
  GetGRNSummary,
  GetPurchaseOrder,
  GetPurchaseVoucher,
  GetVoucherAttachments,
} from "./service/inward_repo";
import LoadingWidget from "../../../../context_providers/modal/loader";
import PurchaseVoucherQRCodeModal from "./components/purchase_voucher_item_qrcode_modal";
import ModalDialog from "../../../../ui/new_components/common/modal";
import "../../../../styles/responsive_shrink.css";

interface PurchaseVoucherProps {
  location?: any;
}

const PurchaseVoucherDetail: FunctionComponent<PurchaseVoucherProps> = (
  props,
) => {
  const { id } = useParams<{ id: string }>();
  const navigation = useHistory();
  const query = new URLSearchParams(props.location.search);
  const po_no = query.get("po_no");
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();
  const [poData, setPoData] = useState<PurchaseOrderModelV2>();
  const [voucherData, setVoucherData] = useState<PurchaseVoucherModelV2>(
    defaultPurchaseValueV2,
  );
  const [existingItems, setExistingItems] = useState<InwardModel[]>([]);
  const [showQRCodeModal, setQRCodeModal] = useState<boolean>(false);
  const [voucherAttachments, setVoucherAttachments] = useState<
    AttachmentModel[]
  >([]);
  const [grnAttachments, setGrnAttachments] = useState<any[]>([]);
  const [projectMap, setProjectMap] = useState<
    Record<string, { project_id: string; project: any }>
  >({});

  const [poItems, setPoItems] = useState([]);
  const [grnMeta, setGrnMeta] = useState<{
    date?: Date;
    created_by_name?: string;
  }>({});

  useEffect(() => {
    if (po_no) {
      getPoData(po_no);
    }
  }, []);

  // Fetch PO data and project data
  const getPoData = async (id: string) => {
    setLoading(true);
    try {
      const res = await GetPurchaseOrder(id);
      if (res.success) {
        if (!res.data.vendor_currency) {
          res.data.vendor_currency = currencies.rupee;
          res.data.vendor_currency_to_inr = 1;
        }

        setPoItems(res.data.items || []);

        setVoucherData((o) => ({
          ...o,
          date: res.data.delivery_date,
          party_id: res.data.party.id,
          party_detail: res.data.party,
          warehouse: res.data.warehouse,
          warehouse_id: res.data.warehouse_id,
          order_detail: {
            ...(o.order_detail || {}), // Ensure order_detail exists
            order_id: "",
            payment_terms: "",
            other_ref: "",
            delivery_terms: "",
            po_no: res.data.po_no,
          },
        }));

        setPoData(res.data);
        setLoading(false);
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error) {
      showToast({
        type: ToastType.error,
        text: "An error occurred while fetching PO data.",
      });
    } finally {
      setLoading(false);
    }
  };

  const getInwards = async (po_no: string) => {
    setLoading(true);
    try {
      const res = await GetPurchaseVoucher(id);

      if (res.success) {
        setExistingItems(res.data.item_movements);
        setGrnMeta({
          date: res.data.date,
          created_by_name: res.data.created_by_name,
        });
        setVoucherData((o) => ({
          ...o,
          due_date: res.data.due_date,
        }));
        setLoading(false);
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error) {
      showToast({
        type: ToastType.error,
        text: "An error occurred while fetching Inward data.",
      });
    } finally {
      setLoading(false);
    }
  };

  const getVoucherAttachments = async (voucher_id: string) => {
    setLoading(true);
    try {
      const res = await GetVoucherAttachments(voucher_id);
      setVoucherAttachments(res.data.attachments);
      if (res.success) {
        setLoading(false);
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error) {
      showToast({
        type: ToastType.error,
        text: "An error occurred while fetching Inward data.",
      });
    } finally {
      setLoading(false);
    }
  };

  const getGrnAttachments = async (poNo: string, voucherId: string) => {
    try {
      const res = await GetGRNSummary(poNo, "GRN");
      if (res.success && Array.isArray(res.data)) {
        const match = res.data.find((g: any) => g.ref_id === voucherId);
        setGrnAttachments(match?.attachments || []);
      }
    } catch (error) {
      // silent: attachments are best-effort here
    }
  };

  useEffect(() => {
    if (poData) {
      getInwards(poData.po_no);
      if (id) {
        getGrnAttachments(poData.po_no, id);
      }
    }

    if (id) {
      getVoucherAttachments(id);
    }
  }, [poData]);

  const mapProjectsToItemMap = (
    existingItems: any[],
    poItems: any[],
  ): Record<string, { project_id: string; project: any }> => {
    if (!existingItems || !poItems) return {};

    const poMap: Record<string, { project_id: string; project: any }> = {};
    poItems.forEach((po) => {
      if (po.item?.item_code) {
        poMap[po.item.item_code] = {
          project_id: po.project_id,
          project: po.project,
        };
      }
    });

    return poMap;
  };

  useEffect(() => {
    if (existingItems && poItems) {
      const map = mapProjectsToItemMap(existingItems, poItems);
      setProjectMap(map);
    }
  }, [existingItems, poItems]);

  const priceMap: Record<string, number> = {};
  (poItems || []).forEach((po: any) => {
    if (po?.item?.item_code) {
      priceMap[po.item.item_code] = po.price ?? 0;
    }
  });

  const cardAttachments = [
    ...(grnAttachments || []).map((a: any) => ({
      url: a.url,
      filename: a.filename,
      type: a.type,
      invoice_number: a.invoice_number,
    })),
    ...(voucherAttachments || []).map((a: any) => ({
      url: a.url,
      filename: a.url ? a.url.split("/").pop() : undefined,
      type: a.type,
      invoice_number: "-",
    })),
  ].filter((a, i, arr) => a.url && arr.findIndex((x) => x.url === a.url) === i);

  return (
    <>
      {showQRCodeModal && (
        <ModalDialog
          onClose={() => setQRCodeModal(false)}
          show={showQRCodeModal}
          title="GRN Item QR Codes"
        >
          <div className="p-4 sm:p-5 w-full md:w-[50vw] max-w-full h-[80vh] overflow-auto">
            <PurchaseVoucherQRCodeModal data={existingItems} />
          </div>
        </ModalDialog>
      )}
      {showOrderDetail && (
        <DrawerWidget
          data={{
            id: 1,
            title: "Order detail",
            type: ModalType.drawer,
            alignment: ModalAlignment.right,
            container: (
              <>
                <div className="p-5 text-xs">
                  <div className="flex flex-col items-start  w-full">
                    <h2 className="text-sm font-semibold">Party</h2>
                    <PartySearchField
                      // show_add={true}
                      value={poData?.party}
                      onSelect={(d) => {
                        setVoucherData((o) => ({
                          ...o,
                          party_detail: d,
                        }));
                      }}
                    />
                  </div>

                  <div className="grid grid-cols-2  my-2 gap-1">
                    <div className=" font-semibold text-gray-500">Party</div>
                    <div className="">: {poData?.party?.name}</div>
                    <div className=" font-semibold text-gray-500">Address</div>
                    <div className="">
                      : {poData?.party?.mail_detail.address.city},
                      {poData?.party?.mail_detail.address.state},
                      {poData?.party?.mail_detail.address.country}-
                      {poData?.party?.mail_detail.address.pincode},
                    </div>
                    <div className=" font-semibold text-gray-500">
                      Contact name
                    </div>
                    <div className="">
                      : {poData?.party?.mail_detail.contact_name}
                    </div>
                    <div className=" font-semibold text-gray-500">
                      Contact mobile
                    </div>
                    <div className="">
                      : {poData?.party?.mail_detail.contact_mobile}
                    </div>
                    <div className=" font-semibold text-gray-500">
                      Contact email
                    </div>
                    <div className="">
                      : {poData?.party?.mail_detail.contact_email}
                    </div>
                  </div>

                  <hr />
                  <h2 className="text-sm font-semibold">Order details</h2>
                  <div className="grid grid-cols-1  my-2 gap-1">
                    <div className="grid grid-cols-4 items-center">
                      <label
                        htmlFor=""
                        className=" font-semibold text-gray-500"
                      >
                        Order ref
                      </label>{" "}
                      <input
                        type="text"
                        className="col-span-3 focus:outline-none focus:ring-2 ring-blue-400 rounded-md py-1 px-2 w-full border"
                        value={voucherData.order_detail?.order_id}
                        onChange={(e) => {
                          setVoucherData((o) => ({
                            ...o,
                            order_detail: {
                              ...o.order_detail!,
                              order_id: e.target.value,
                            },
                          }));
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center">
                      <label
                        htmlFor=""
                        className=" font-semibold text-gray-500"
                      >
                        Delivery terms
                      </label>{" "}
                      <textarea
                        className="col-span-3 focus:outline-none focus:ring-2 ring-blue-400 rounded-md py-1 px-2 w-full border"
                        value={voucherData.order_detail?.delivery_terms}
                        onChange={(e) => {
                          setVoucherData((o) => ({
                            ...o,
                            order_detail: {
                              ...o.order_detail!,
                              delivery_terms: e.target.value,
                            },
                          }));
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center">
                      <label
                        htmlFor=""
                        className=" font-semibold text-gray-500"
                      >
                        Payment terms
                      </label>{" "}
                      <textarea
                        className="col-span-3 focus:outline-none focus:ring-2 ring-blue-400 rounded-md py-1 px-2 w-full border"
                        value={voucherData.order_detail?.payment_terms}
                        onChange={(e) => {
                          setVoucherData((o) => ({
                            ...o,
                            order_detail: {
                              ...o.order_detail!,
                              payment_terms: e.target.value,
                            },
                          }));
                        }}
                      />
                    </div>
                  </div>
                </div>
              </>
            ),
          }}
          onClose={function (): void {
            setShowOrderDetail(false);
          }}
        ></DrawerWidget>
      )}
      <div className="mobile-shrink mt-2 px-2 md:px-6">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-2">
          {/* <div className="font-bold text-2xl text-gray-500 border-l-4 border-myPrimaryColor pl-2">
            Goods Received Note
          </div> */}
          <div className="flex flex-row">
            <button
              className="  flex  items-center p-2  hover:text-gray-700  font-bold text-lg"
              onClick={() => {
                navigation.goBack();
              }}
            >
              <Icon path={mdiChevronLeft} className="h-6 w-6 font-bold"></Icon>{" "}
              Back
            </button>
            <div className="text-xl font-bold border-l-4 border-myPrimaryColor p-2">
              Goods Received Note
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <label htmlFor="" className="text-sm font-semibold text-gray-500">
              Date
            </label>{" "}
            <input
              disabled={true}
              type="date"
              className=" focus:outline-none focus:ring-2 ring-blue-400 rounded-md py-1 px-2 w-full text-sm border"
              value={
                voucherData.date
                  ? moment(voucherData.date).format("YYYY-MM-DD")
                  : ""
              }
              onChange={(e) => {
                const date = UTCToLocalDate(e.target.value)!;
                setVoucherData((o) => ({
                  ...o,
                  date: date,
                }));
              }}
            />
          </div>
        </div>
        <div className="bg-white rounded-lg p-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 md:gap-x-12 gap-y-2 items-start justify-start w-full md:w-2/3">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const d = formToObject(new FormData(e.currentTarget));
                if (d["po_no"]) {
                  if (existingItems!.length > 0) {
                    const confirm = window.confirm(
                      "Are you sure to refresh po data?",
                    );
                    if (!confirm) {
                      return;
                    }
                  }
                  getPoData(d["po_no"]);
                }
              }}
              className="grid grid-cols-1 items-center"
            >
              <label
                htmlFor="invoice_no"
                className="text-sm font-semibold text-gray-500"
              >
                PO number
              </label>

              <div className="flex gap-1 col-span-3 items-center">
                <input
                  disabled={true}
                  value={voucherData.order_detail?.po_no}
                  onChange={(e) => {
                    setVoucherData((o) => ({
                      ...o,
                      order_detail: {
                        ...o.order_detail!,
                        po_no: e.target.value,
                      },
                    }));
                  }}
                  required
                  placeholder="Po number"
                  name="po_no"
                  type="text"
                  className="focus:outline-none focus:ring-2 ring-blue-400 rounded-md py-1 px-2 w-full border"
                />
                <button
                  title="Fetch po details"
                  className="rounded-full p-1 border"
                >
                  <Icon
                    path={mdiRefresh}
                    size={1}
                    className="hover:scale-110 transform"
                  ></Icon>
                </button>
              </div>
            </form>
            <div className="grid grid-cols-1 items-center">
              <label htmlFor="" className="text-sm font-semibold text-gray-500">
                Delivery Date
              </label>{" "}
              <input
                type="date"
                disabled={true}
                className=" focus:outline-none focus:ring-2 ring-blue-400 rounded-md py-1 px-2 w-full text-sm border"
                value={
                  voucherData.due_date
                    ? moment(voucherData.due_date).format("YYYY-MM-DD")
                    : "--"
                }
                onChange={(e) => {
                  const date = UTCToLocalDate(e.target.value)!;
                  setVoucherData((o) => ({
                    ...o,
                    due_date: date,
                  }));
                }}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 md:gap-x-12 gap-y-2 mt-3 items-start justify-start w-full md:w-2/3">
            <div className="grid grid-cols-1 items-center">
              <label htmlFor="" className="text-sm font-semibold text-gray-500">
                Vendor
              </label>
              <div className="col-span-3">
                <PartySearchField
                  disabled={true}
                  key={Math.random() * 10000}
                  value={poData?.party ? poData.party : undefined}
                  onSelect={(d) => {
                    setVoucherData((o) => ({
                      ...o,
                      // party_detail: party,
                    }));
                  }}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 items-center">
              <label
                htmlFor=""
                className="text-sm font-semibold text-gray-500 "
              >
                Warehouse
              </label>
              <div className="col-span-3">
                <input
                  disabled={true}
                  value={voucherData?.warehouse?.name}
                  required
                  placeholder="Warehouse"
                  type="text"
                  className="focus:outline-none focus:ring-2 ring-blue-400 rounded-md py-1 px-2 w-full border"
                />
              </div>
            </div>
          </div>
          {/* <div className="flex justify-end">
            <div
              onClick={() => {
                setShowOrderDetail(true);
              }}
              className="flex gap-5 border rounded items-center px-2 py-1  text-sm cursor-pointer"
            >
              <p>Order details</p>
              <Icon path={mdiChevronRight} className="w-4 h-4" />
            </div>
          </div> */}
        </div>
        <div className="relative">
          <div className="mt-3 mb-3 border-b border-gray-300 text-sm font-bold">
            Item details
          </div>

          {/* Item cards */}
          {existingItems && existingItems.length > 0 ? (
            <>
              {/* Web: single GRN summary card (matches warehouse GRN history) */}
              <div className="hidden md:block mt-2">
                <GRNSummaryCard
                  grnNo={id}
                  date={grnMeta.date ?? voucherData.date}
                  createdByName={grnMeta.created_by_name}
                  items={existingItems}
                  projectMap={projectMap}
                  priceMap={priceMap}
                  attachments={cardAttachments}
                />
              </div>

              {/* Mobile: existing per-item cards (unchanged) */}
              <div className="md:hidden grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mt-2">
                {existingItems.map((item, i) => {
                  let tempItem: any = item;
                  const projectInfo = projectMap[tempItem.item_code]; // Only for this item
                  return (
                    <GRNItemCard
                      key={item.id ?? i}
                      item={item}
                      projectDetails={projectInfo}
                    />
                  );
                })}
              </div>
            </>
          ) : (
            <div className="text-center text-gray-400 py-8 text-sm">
              No items found
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 justify-end px-2 md:px-6 py-2">
        <button
          type="button"
          onClick={() => setQRCodeModal(true)}
          className="px-6 sm:px-10 py-1 rounded-md bg-myPrimaryColor text-white text-sm "
        >
          QR Code
        </button>
        <button
          type="button"
          onClick={() => {
            navigation.goBack();
          }}
          className="px-6 sm:px-10 py-1 rounded-md bg-white border text-black text-sm "
        >
          Quit
        </button>
      </div>
      {loading && <LoadingWidget />}
    </>
  );
};

export default PurchaseVoucherDetail;
